import { useEffect, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { CalendarX, Loader2 } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import type { Json } from "@/integrations/supabase/types";
import CancellationPolicyBlock from "@/components/event-detail/CancellationPolicyBlock";

interface AppSettingRow {
  key: string;
  value: Json;
}

const readDays = (value: Json | undefined): string => {
  if (typeof value === "number") return String(value);
  if (typeof value === "string") return value;
  if (value && typeof value === "object" && !Array.isArray(value)) {
    const days = value.days;
    if (typeof days === "number") return String(days);
    if (typeof days === "string") return days;
  }
  return "7";
};

const readEnabled = (value: Json | undefined): boolean => {
  if (typeof value === "boolean") return value;
  if (typeof value === "string") return value === "true";
  if (value && typeof value === "object" && !Array.isArray(value)) {
    const enabled = value.enabled;
    if (typeof enabled === "boolean") return enabled;
    if (typeof enabled === "string") return enabled === "true";
  }
  return true;
};

const readPolicyText = (value: Json | undefined): string => {
  if (typeof value === "string") return value;
  if (value && typeof value === "object" && !Array.isArray(value)) {
    const text = value.text;
    return typeof text === "string" ? text : "";
  }
  return "";
};

const KEYS = [
  "ticket_cancellation_window_days",
  "ticket_late_cancel_enabled",
  "ticket_cancellation_policy_text",
] as const;

export default function CancellationPolicySettings() {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const { data, isLoading } = useQuery({
    queryKey: ["app-settings", "cancellation-policy-admin"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("app_settings")
        .select("key, value")
        .in("key", KEYS as unknown as string[]);
      if (error) throw error;
      return (data ?? []) as AppSettingRow[];
    },
  });

  const [windowDays, setWindowDays] = useState<string>("7");
  const [lateEnabled, setLateEnabled] = useState<boolean>(true);
  const [policyText, setPolicyText] = useState<string>("");

  useEffect(() => {
    if (!data) return;
    const byKey = new Map(data.map((r) => [r.key, r.value]));
    const w = byKey.get("ticket_cancellation_window_days");
    const l = byKey.get("ticket_late_cancel_enabled");
    const t = byKey.get("ticket_cancellation_policy_text");
    setWindowDays(readDays(w));
    setLateEnabled(readEnabled(l));
    setPolicyText(readPolicyText(t));
  }, [data]);

  const saveMutation = useMutation({
    mutationFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      const parsedDays = Math.max(0, Math.min(60, Math.floor(Number(windowDays) || 0)));
      const now = new Date().toISOString();

      const updates = [
        supabase
          .from("app_settings")
          .update({ value: { days: parsedDays } as unknown as Json, updated_at: now, updated_by: user?.id ?? null })
          .eq("key", "ticket_cancellation_window_days")
          .select("key"),
        supabase
          .from("app_settings")
          .update({ value: { enabled: lateEnabled } as unknown as Json, updated_at: now, updated_by: user?.id ?? null })
          .eq("key", "ticket_late_cancel_enabled")
          .select("key"),
        supabase
          .from("app_settings")
          .update({ value: { text: policyText.trim() } as unknown as Json, updated_at: now, updated_by: user?.id ?? null })
          .eq("key", "ticket_cancellation_policy_text")
          .select("key"),
      ];

      const results = await Promise.all(updates);
      const error = results.find((result) => result.error)?.error;
      if (error) throw error;
      if (results.some((result) => (result.data ?? []).length !== 1)) {
        throw new Error("Cancellation policy settings are missing. Please reload and try again.");
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["app-settings", "cancellation-policy-admin"] });
      queryClient.invalidateQueries({ queryKey: ["cancellation-policy-settings"] });
      toast({ title: "Saved", description: "Cancellation policy updated." });
    },
    onError: (error: any) => {
      console.error("Failed to save cancellation policy:", error);
      toast({
        title: "Error",
        description: error?.message ?? "Failed to save. Please try again.",
        variant: "destructive",
      });
    },
  });

  // Preview against a date 14 days from now so users see the "refund" branch copy
  const previewDate = new Date(Date.now() + 14 * 24 * 60 * 60 * 1000).toISOString();

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <CalendarX className="h-5 w-5" />
          Ticket Cancellation Policy
        </CardTitle>
        <CardDescription>
          Control the self-serve cancellation window and copy shown on event pages and in the cancel dialog.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {isLoading ? (
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" /> Loading…
          </div>
        ) : (
          <>
            <div className="space-y-2">
              <Label htmlFor="cancel-window-days">Cancellation window (days before event)</Label>
              <Input
                id="cancel-window-days"
                type="number"
                min={0}
                max={60}
                value={windowDays}
                onChange={(e) => setWindowDays(e.target.value)}
                className="max-w-[160px]"
              />
              <p className="text-xs text-muted-foreground">
                Users who cancel at least this many days before the event get a full credit refund.
              </p>
            </div>

            <div className="flex items-start justify-between gap-4 rounded-lg border p-4">
              <div className="space-y-0.5">
                <Label htmlFor="late-cancel-toggle" className="text-base font-medium">
                  Allow late cancellations (no refund)
                </Label>
                <p className="text-sm text-muted-foreground">
                  When on, users can still cancel inside the window to release their seat — no credits refunded.
                  When off, they see a contact-us block instead.
                </p>
              </div>
              <Switch
                id="late-cancel-toggle"
                checked={lateEnabled}
                onCheckedChange={setLateEnabled}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="cancel-policy-text">Custom policy text (optional)</Label>
              <Textarea
                id="cancel-policy-text"
                rows={3}
                value={policyText}
                onChange={(e) => setPolicyText(e.target.value)}
                placeholder="Leave blank to use the auto-generated default based on the settings above."
              />
              <p className="text-xs text-muted-foreground">
                Shown verbatim on event pages when set. Clear to fall back to the default copy.
              </p>
            </div>

            <div>
              <Label className="text-sm font-medium">Preview (event 14 days from now)</Label>
              <div className="mt-2">
                <CancellationPolicyBlock eventStartTime={previewDate} />
              </div>
            </div>

            <div className="flex justify-end">
              <Button onClick={() => saveMutation.mutate()} disabled={saveMutation.isPending}>
                {saveMutation.isPending ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" /> Saving…
                  </>
                ) : (
                  "Save changes"
                )}
              </Button>
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
}
